import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * How often a live view asks again, in milliseconds.
 *
 * SC-002 asks that a completed hole appear within five minutes. Ten seconds
 * clears that by more than an order of magnitude, and it is chosen against the
 * other end too: a league night is five people at one simulator, so this is one
 * small GET every ten seconds from a handful of phones. There is no cost worth
 * trading a member's patience against (FR-035b).
 */
export const POLL_INTERVAL_MS = 10_000

/** The longest a failing poll waits before trying again. */
export const MAX_BACKOFF_MS = 60_000

export interface Polled<T> {
  /**
   * The last state that loaded successfully. **It is kept when a later load
   * fails** (FR-035f, SC-002a) — the scoreboard on the garage screen must never
   * go blank because the API blinked.
   */
  data: T | null
  /** When `data` arrived. Shown beside it, so nothing on screen is undated. */
  loadedAt: Date | null
  /** Nothing has loaded yet. Distinct from having loaded and gone stale. */
  loading: boolean
  /** Consecutive failures since the last success; 0 while healthy. */
  failures: number
  /** The most recent failure, kept for its message and request id. */
  error: Error | null
  /**
   * Whether this view is still asking. False once the interval function has
   * said there is nothing more to ask for — which is what lets a page avoid
   * printing "updates on its own" over a record that cannot change.
   */
  polling: boolean
  /** Ask again now, without waiting for the next tick. */
  refresh: () => void
}

/**
 * usePolling re-reads a route on a timer and never throws away what it has.
 *
 * # Updating with no interaction (FR-035b, SC-014)
 *
 * The live views are opened and then left alone — on a phone at the simulator,
 * on the garage's second screen. Nothing on them is clicked, so the data has to
 * arrive on its own.
 *
 * # A failure is a degradation, never a blank and never an error page
 *
 * Constitution IV, and FR-035e/FR-035f say it exactly: when an update fails,
 * the last known good state stays on screen **with its timestamp**, and
 * updating resumes automatically when the source recovers. So a failed load
 * here does not clear `data`; it increments `failures`, which is what a view
 * renders a staleness banner from. A view showing a spinner or an error where a
 * scoreboard was is the failure this exists to prevent.
 *
 * # Backoff, because a dead API should not be hammered
 *
 * Each consecutive failure doubles the wait, to a minute. The first success
 * resets it, so recovery is noticed within one ordinary cycle rather than after
 * whatever the backoff had grown to.
 *
 * # The interval may be a FUNCTION of what loaded, and may say "stop"
 *
 * A round nobody is playing has nothing to report, and a view that keeps asking
 * is a tab refreshing forever over a record that cannot change. The state that
 * decides this is only known once a response has landed, so it cannot be a
 * dependency: putting it in `deps` moves the key and hands back a blank frame.
 * So the interval is computed FROM the data, and `null` means there is nothing
 * more to ask for — the polling counterpart of `useApi(null)`.
 *
 * Stopping is not unmounting. `refresh()` still works afterwards, so a view
 * that has stopped can still be asked again deliberately.
 *
 * # One request in flight at a time
 *
 * The next tick is scheduled when the previous load SETTLES, not on a fixed
 * interval. An interval that fires while a slow request is outstanding queues a
 * second one behind it, and on a phone on garage wifi that is how a view ends
 * up rendering an older response after a newer one.
 */
export function usePolling<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: unknown[],
  interval: number | ((data: T) => number | null) = POLL_INTERVAL_MS,
): Polled<T> {
  const key = JSON.stringify(deps)
  const [held, setHeld] = useState<{
    key: string
    data: T | null
    loadedAt: Date | null
    failures: number
    error: Error | null
    polling: boolean
  }>({ key, data: null, loadedAt: null, failures: 0, error: null, polling: true })

  // `load` is a new closure on every render, and depending on it would restart
  // the loop each time — which is a poll every render, not every ten seconds.
  // The ref holds the latest one; the effect depends on the key alone.
  const loadRef = useRef(load)
  // Same reasoning as `loadRef`: an inline arrow here is a new value every
  // render, and the loop must depend on the key alone.
  const intervalRef = useRef(interval)
  // Assigned in an effect rather than during render: a ref written while
  // rendering is a side effect, and React's own lint rule says so. Effects run
  // in declaration order, so this one is current before the loop below starts.
  useEffect(() => {
    loadRef.current = load
    intervalRef.current = interval
  })

  const wake = useRef<(() => void) | null>(null)
  const refresh = useCallback(() => wake.current?.(), [])

  useEffect(() => {
    const controller = new AbortController()
    let timer: ReturnType<typeof setTimeout> | undefined
    let stopped = false
    let failures = 0
    // Read through the ref, not the closure: naming `interval` in here would
    // put it in the effect's dependencies, and the loop must depend on the
    // key alone or it restarts on every render.
    let base = typeof intervalRef.current === 'number' ? intervalRef.current : POLL_INTERVAL_MS

    const schedule = (ms: number) => {
      if (stopped) return
      clearTimeout(timer)
      timer = setTimeout(tick, ms)
    }

    const tick = () => {
      loadRef
        .current(controller.signal)
        .then((data) => {
          if (stopped) return
          failures = 0
          const i = intervalRef.current
          const next = typeof i === 'function' ? i(data) : i
          setHeld({
            key,
            data,
            loadedAt: new Date(),
            failures: 0,
            error: null,
            polling: next !== null,
          })
          // **`stopped` is deliberately NOT set here.** It means unmounted, and
          // it gates the abort and the `setHeld` guards; a view that has simply
          // run out of things to ask for must still honour `refresh()`.
          if (next === null) return
          base = next
          schedule(next)
        })
        .catch((err: unknown) => {
          // An abort is this view going away, not a failure to report.
          if (stopped || (err instanceof DOMException && err.name === 'AbortError')) return
          failures += 1
          const error = err instanceof Error ? err : new Error(String(err))
          // **`data` is untouched.** Whatever last loaded stays on screen with
          // its own timestamp (FR-035e, FR-035f).
          setHeld((prev) => ({ ...prev, failures, error, polling: true }))
          // **Untouched by the interval function on purpose.** A failed load
          // tells you nothing about the event's state, so it must keep
          // retrying -- and it backs off from whatever cadence was in use, so a
          // slowly-polled view does not suddenly start hammering.
          schedule(Math.min(base * 2 ** (failures - 1), MAX_BACKOFF_MS))
        })
    }

    wake.current = () => schedule(0)
    tick()
    return () => {
      stopped = true
      wake.current = null
      clearTimeout(timer)
      controller.abort()
    }
  }, [key])

  // A changed key means the previous view's data is not this one's. Comparing
  // during render rather than clearing in an effect avoids a frame in which the
  // old event's scoreboard is shown under the new event's heading.
  if (held.key !== key) {
    return {
      data: null,
      loadedAt: null,
      loading: true,
      failures: 0,
      error: null,
      polling: true,
      refresh,
    }
  }
  return {
    data: held.data,
    loadedAt: held.loadedAt,
    loading: held.data === null && held.error === null,
    failures: held.failures,
    error: held.error,
    polling: held.polling,
    refresh,
  }
}
